import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const features = [
  {
    icon: 'ri-price-tag-3-fill',
    title: 'Fixed Upfront Pricing',
    description: 'The price you see is the price you pay. No surveys needed, no day-of surprises.',
  },
  {
    icon: 'ri-eye-off-fill',
    title: 'No Hidden Costs',
    description: 'Installation, materials, certification — it\'s all included. Zero extras.',
  },
  {
    icon: 'ri-calendar-check-fill',
    title: 'Fast Installation Slots',
    description: 'Most jobs completed within 48 hours of booking. We work around your schedule.',
  },
  {
    icon: 'ri-user-star-fill',
    title: 'Fully Qualified Engineers',
    description: 'All installs by MCS, NAPIT and OZEV-certified engineers. Vetted and insured.',
  },
  {
    icon: 'ri-shield-check-fill',
    title: 'Warranty Included',
    description: '10-year warranty on all hardware and installation. Peace of mind, guaranteed.',
  },
  {
    icon: 'ri-smartphone-fill',
    title: 'Smart Monitoring',
    description: 'Track your energy generation and savings in real-time from your phone.',
  },
];

export default function FeatureBlock() {
  const headingAnim = useScrollAnimation();
  const gridAnim = useScrollAnimation();
  const certAnim = useScrollAnimation();

  return (
    <section className="bg-[#1F2933] py-16 md:py-24 overflow-hidden relative">
      {/* Decorative bg elements */}
      <div className="absolute top-0 left-0 w-80 h-80 rounded-full bg-[#4CAF50]/5 -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-[#4CAF50]/5 translate-x-1/3 translate-y-1/3 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-6 relative">
        <div
          ref={headingAnim.ref}
          className={`text-center mb-12 transition-all duration-700 ${headingAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <p className="text-[#7AC943] font-semibold text-sm uppercase tracking-widest mb-2">Why Choose Us</p>
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-white">
            The Renewables Made Easy Difference
          </h2>
          <p className="text-white/60 mt-3 text-base max-w-xl mx-auto">
            We&apos;ve stripped away the complexity so you get a better deal, faster.
          </p>
        </div>

        <div ref={gridAnim.ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((feat, i) => (
            <div
              key={feat.title}
              style={{ transitionDelay: `${i * 90}ms` }}
              className={`bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:-translate-y-1 hover:border-[#4CAF50]/40 transition-all duration-300 ${gridAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#4CAF50]/20 mb-4 group-hover:bg-[#4CAF50]/40 transition-colors">
                <i className={`${feat.icon} text-[#7AC943] text-2xl`} />
              </div>
              <h3 className="font-heading font-bold text-white text-base mb-2">{feat.title}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{feat.description}</p>
            </div>
          ))}
        </div>

        {/* Certification logos strip */}
        <div
          ref={certAnim.ref}
          className={`mt-12 pt-10 border-t border-white/10 transition-all duration-700 ${certAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <p className="text-white/40 text-xs text-center uppercase tracking-widest mb-6">Certified &amp; Approved By</p>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-14">
            {['MCS Certified', 'NAPIT Approved', 'OZEV Authorised', 'Which? Trusted Trader', 'TrustMark'].map((cert, i) => (
              <div
                key={cert}
                style={{ transitionDelay: `${i * 80}ms` }}
                className={`flex items-center gap-2 transition-all duration-500 ${certAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-medal-fill text-[#7AC943] text-base" />
                </div>
                <span className="text-white/70 text-sm font-medium whitespace-nowrap">{cert}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
